import { Paragraph, Toggle, Tooltip } from '@cue/atoms';
import { AnalyticsTransformer, LocalStorageUtil } from '@cue/utility';
import styled from '@emotion/styled';
import { useQueryClient } from '@tanstack/react-query';
import { navigate } from 'gatsby';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const OnSiteToggle = React.forwardRef<OnSiteToggleHandle, OnsiteToggleProps>(
  ({ lang }, ref) => {
    const [onSiteMode, setOnSiteMode] = useState<boolean>(
      (LocalStorageUtil.get('onSiteMode') as boolean) || false,
    );
    const { t } = useTranslation();
    const [active, setActive] = useState(LocalStorageUtil.get('onSiteMode') !== null);

    const queryClient = useQueryClient();

    React.useImperativeHandle(ref, () => {
      const impReturn: OnSiteToggleHandle = {
        update: () => reCheck(),
      };
      return impReturn;
    }, []);
    const reCheck = () => {
      const ls = LocalStorageUtil.get('onSiteMode');
      setActive(ls !== null);
      setOnSiteMode(ls as boolean);
    };

    useEffect(() => {
      LocalStorageUtil.set('onSiteMode', onSiteMode);
      queryClient.invalidateQueries({ queryKey: ['onsite-mode'] });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onSiteMode]);

    if (!active) return;
    return (
      <OnSiteToggleContainer className="cue-project-onsite-toggle" data-tooltip-id="onsite-mode">
        <span>On-site Mode</span>
        <Toggle
          data-test-id="cue-test-onsite-toggle"
          onChecked={(checked) => {
            AnalyticsTransformer.trackEvent({
              category: 'general',
              type: 'onsite-mode',
              action: checked ? 'start' : 'end',
            });
            setOnSiteMode(checked);
            if (checked) {
              navigate(`/${lang}/onsite-mode`);
            } else {
              navigate(`/${lang}/`);
            }
          }}
          checked={onSiteMode}
        />
        {!onSiteMode && (
          <Tooltip place="bottom" id="onsite-mode" className="cue-project-onsite-toggle-tooltip">
            <div>
              <Paragraph>{t('onsite.tooltip.enabled.heading')}</Paragraph>
              <Paragraph>{t('onsite.tooltip.enabled.description')}</Paragraph>
            </div>
          </Tooltip>
        )}
      </OnSiteToggleContainer>
    );
  },
);
OnSiteToggle.displayName = 'OnSiteToggle';
export interface OnSiteToggleHandle {
  update(): void;
}

export interface OnsiteToggleProps {
  lang: string;
}

const OnSiteToggleContainer = styled.div``;
